import {
  WSButton,
  WSButtons,
  WSCentered,
  WSContainer,
  WSFormOld,
  WSRadioInputGroup,
  WSStepper,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { BrowserPageTitle } from "../../components/BrowserPageTitle/BrowserPageTitle";
import { ProjectOnboardingLayout } from "../../components/ProjectOnboardingLayout/ProjectOnboardingLayout";
import { QUERY_BOOKKEEPING_BANKING_ACCOUNT } from "../../query/bookkeeping/keys";
import { useWSMutation } from "../../query/helpers";
import { useUserId } from "../../query/hooks/helpers";
import { QUERY_PAYOUT_SETTINGS } from "../../query/payments/keys";
import { useUpdateActivity } from "../../query/users/mutations";
import { paymentsService } from "../../services/payments";
import styles from "./WalletSetupDistribution.module.scss";

export const WalletSetupDistribution: React.FC = () => {
  const userId = useUserId();
  const history = useHistory();
  const [updateActivity] = useUpdateActivity(userId);

  const [submit, submitMeta] = useWSMutation<
    any,
    any,
    {
      values: {
        incomeType?: string;
        rate?: number;
      };
    }
  >(
    async ({ values }) => {
      let walletDistibution;
      if (values.incomeType === "ALL") {
        walletDistibution = 100;
      } else if (values.incomeType === "NO") {
        walletDistibution = 0;
      } else {
        walletDistibution = values.rate || 0;
      }

      await paymentsService.payoutSettings.updateWalletDistribution(
        userId,
        walletDistibution
      );

      await updateActivity({
        flows: {
          walletSetup: {
            complete: true,
            version: 2,
            currentStep: 2,
            totalSteps: 2
          }
        }
      });

      history.push("/member/wallet");
    },
    {
      dependencies: [QUERY_PAYOUT_SETTINGS, QUERY_BOOKKEEPING_BANKING_ACCOUNT]
    }
  );

  return (
    <ProjectOnboardingLayout progress={0.1}>
      <BrowserPageTitle title="Setup Wingspan Wallet" />
      <WSContainer verticalPadding>
        <WSFormOld
          onSubmit={async formData => {
            submit({ values: formData });
          }}
          defaultValues={{
            incomeType: "ALL",
            rate: 30
          }}
          validationSchema={Yup.object().shape({
            rate: Yup.number()
              .min(0)
              .max(100)
          })}
        >
          <WSCentered span={{ m: "6" }}>
            <WSText.ParagraphSm color="gray500" mt="M" pt="M">
              1 of 1
            </WSText.ParagraphSm>
            <WSText.Heading4 mt="XL">
              How much of your income would you like deposited into your
              Wingspan Wallet account?
            </WSText.Heading4>

            <WSFormOld.Field
              name="incomeType"
              mt="2XL"
              component={WSRadioInputGroup}
              componentProps={{
                radioInputClassName: styles.option,
                options: [
                  {
                    label: "All income",
                    value: "ALL"
                  },
                  {
                    label: "Some income",
                    value: "SOME"
                  }
                ]
              }}
            />
          </WSCentered>

          <WSFormOld.Value name="incomeType">
            {incomeType =>
              incomeType === "SOME" ? (
                <WSCentered span={{ s: "8", m: "6", l: "4" }}>
                  <WSText mt="XL" color="gray400" align="center">
                    Select income
                  </WSText>
                  <WSFormOld.Field
                    name="rate"
                    component={WSStepper}
                    componentProps={{ min: 0, max: 100, postfix: "%" }}
                  />
                </WSCentered>
              ) : null
            }
          </WSFormOld.Value>

          <WSCentered span={{ m: "6" }}>
            <WSText color="gray500" mt="2XL" align="center">
              Don’t worry, you can always change this again later.
            </WSText>
          </WSCentered>

          <WSCentered span={{ m: "6" }}>
            <WSButtons justify="center" mt="3XL">
              <WSFormOld.SubmitButton
                fullWidth
                name="finishSetup"
                disabled={submitMeta.isLoading}
              >
                Finish Setup
              </WSFormOld.SubmitButton>
            </WSButtons>
          </WSCentered>
          <WSCentered span={{ m: "4" }}>
            <WSButtons justify="center" mt="3XL">
              <WSButton.Link
                disabled={submitMeta.isLoading}
                onAsyncClick={() => submit({ values: { rate: 0 } })}
              >
                Don't want to set aside?{" "}
                <u>Create a wallet without automatic deposits.</u>
              </WSButton.Link>
            </WSButtons>
          </WSCentered>
        </WSFormOld>
      </WSContainer>
    </ProjectOnboardingLayout>
  );
};
